import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import type { DimensionView } from '../interpretiveReportView';
import { SectionHead } from './SectionHead';
import { Callout, toneClass } from './Tone';
import { agreementTones } from '../../../components/AnalysisLayout/analysis.utils';
import styles from '../InterpretiveReportPage.module.css';

type DimensionSectionProps = {
  number: string;
  dimension: DimensionView;
  scaleMax: string;
};

/** One page per dimension: the numbers, what they mean in everyday work, and a question to reflect on */
export const DimensionSection = ({ number, dimension, scaleMax }: DimensionSectionProps) => {
  const { t } = useTranslation();
  const levelScore = t(`interpretiveReport.document.levelScore.${dimension.status}`);
  const agreement = t(`teamResult.agreement.${dimension.agreement}`);
  const agreementInline = t(`teamResult.agreementInSentence.${dimension.agreement}`);

  return (
    <section>
      <SectionHead
        number={number}
        eyebrow={dimension.name}
        title={dimension.name}
        intro={t('interpretiveReport.document.dimension.subtitle', { level: levelScore, agreement })}
      />
      <div className={cn(styles.twoColumns, styles.keep)}>
        <div className={styles.factBox}>
          <span className={styles.label}>{t('interpretiveReport.document.dimension.score')}</span>
          <span className={styles.factScore}>
            {dimension.score}
            <span className={styles.factOutOf}>/{scaleMax}</span>
          </span>
          <div aria-hidden="true" className={styles.bar}>
            <div
              className={cn(styles.barFill, toneClass(dimension.status, 'Fill'))}
              style={{ width: `${dimension.barPercent}%` }}
            />
          </div>
        </div>
        <div className={styles.factBox}>
          <span className={styles.label}>{t('interpretiveReport.document.dimension.agreement')}</span>
          <span className={styles.factValue}>{agreement}</span>
          <span className={styles.meta}>
            {t('interpretiveReport.document.dimension.standardDeviation', { sd: dimension.standardDeviation })}
          </span>
        </div>
      </div>

      <div className={cn(styles.sectionCallout, styles.keep)}>
        {/* The score and the agreement each in the colour of their own, as on the overview and every page */}
        <Callout
          tone={dimension.status}
          lead={t(`interpretiveReport.document.dimension.verdict.${dimension.status}`)}
        >
          {`${levelScore}.`}
        </Callout>
        <Callout tone={agreementTones[dimension.agreement]} lead={`${agreement}.`}>
          {t(`teamResult.agreementExplanation.${dimension.agreement}`)}
        </Callout>
      </div>

      <div className={styles.reading}>
        <div className={cn(styles.readingPart, styles.keep)}>
          <h3 className={styles.readingTitle}>{t('interpretiveReport.document.dimension.numbersTitle')}</h3>
          <p className={styles.readingText}>
            {t('interpretiveReport.document.dimension.numbers', {
              level: levelScore,
              score: dimension.score,
              max: scaleMax,
              agreement: agreementInline,
              sd: dimension.standardDeviation,
            })}
          </p>
        </div>
        <div className={cn(styles.readingPart, styles.keep)}>
          <h3 className={styles.readingTitle}>{t('interpretiveReport.document.dimension.everydayTitle')}</h3>
          <p className={styles.readingText}>{dimension.everydayImpact}</p>
        </div>
        <div className={cn(styles.reflection, styles.keep)}>
          <h3 className={styles.reflectionTitle}>{t('interpretiveReport.document.dimension.reflectionTitle')}</h3>
          <p className={styles.reflectionQuestion}>{dimension.reflectionQuestion}</p>
        </div>
      </div>
    </section>
  );
};
